import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import Sidebar from "../components/Dashboard/Sidebar/Sidebar";
import MobileTopBar from "../components/Dashboard/MobileTopBar/MobileTopBar";
import { useSidebar, MOBILE_TOPBAR_HEIGHT } from "../contexts/SidebarContext";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { ErrorBoundary } from "../components/common/ErrorBoundary";
import { TopBarSlotProvider } from "../contexts/TopBarSlotContext";

const ProtectedLayout: React.FC = () => {
  const { sidebarWidth } = useSidebar();
  const { isLargeDesktop } = useBreakpoint();
  const location = useLocation();

  // Mobile layout (centred top bar + drawer, full-width content) for phones,
  // tablets AND iPad-sized screens; only true desktops (lg, >=1200px) get the
  // fixed sidebar. The full desktop layout is unusably cramped at tablet widths.
  const useMobileLayout = !isLargeDesktop;

  return (
    <TopBarSlotProvider>
    <Box sx={{ display: "flex", width: "100%", maxWidth: "100vw", overflowX: "hidden" }}>
      {/* Mobile: TopBar + Drawer, Desktop: Fixed Sidebar */}
      {useMobileLayout && <MobileTopBar />}
      <Sidebar isMobileMode={useMobileLayout} />

      <Box
        component="main"
        sx={{
          // Mobile: full width with top padding, Desktop: offset by sidebar
          marginLeft: useMobileLayout ? 0 : `${sidebarWidth}px`,
          // env(safe-area-inset-top) matches the top bar's notch clearance
          // in the native app and resolves to 0 in ordinary browsers
          marginTop: useMobileLayout
            ? `calc(${MOBILE_TOPBAR_HEIGHT}px + env(safe-area-inset-top, 0px))`
            : 0,
          width: useMobileLayout ? "100%" : `calc(100% - ${sidebarWidth}px)`,
          maxWidth: useMobileLayout ? "100vw" : `calc(100vw - ${sidebarWidth}px)`,
          minHeight: useMobileLayout
            ? `calc(100dvh - ${MOBILE_TOPBAR_HEIGHT}px - env(safe-area-inset-top, 0px))`
            : "100vh",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "#1a1a1a" // Grey flat UI dark primary
              : "#ffffff", // Pure white
          transition: useMobileLayout
            ? "none"
            : "margin-left 0.3s ease, width 0.3s ease",
          // Prevent horizontal overflow on mobile
          overflowX: "hidden",
        }}
      >
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </Box>
    </Box>
    </TopBarSlotProvider>
  );
};

export default ProtectedLayout;
